import { isRevertCommit, parseRevertedMessage } from "../git/CommitDetection.js";
import type {
    ApplyOptions,
    ReplayService,
    ReplayOptions,
    ReplayPreparation,
    ReplayReport,
} from "../ReplayService.js";
import { assertActive, computeCommitBuckets, dedupeDegradedReasons } from "../ReplayService.js";
import type { Flow } from "./Flow.js";

/**
 * The full regeneration pipeline:
 *
 *   pre-rebase → dedup → detect → apply → strip-markers → post-rebase → save → commit
 *
 * Pre-rebase runs BEFORE `commitGeneration` while HEAD has customer code.
 * Post-rebase runs AFTER `applyPatches` against the new generation tree.
 * The two phases share concerns (revert detection, content refresh, dedup,
 * user-owned classification) but operate on different reference states —
 * unifying them is tracked as FER-10387 (deferred post-GA).
 */
export class NormalRegenerationFlow implements Flow {
    constructor(private readonly service: ReplayService) {}

    async prepare(options?: ReplayOptions): Promise<ReplayPreparation> {
        const preLock = this.service.lockManager.read();
        // Export a REACHABLE baseline: the recorded current_generation can be an orphaned
        // local SHA after signed-push + squash-merge. See resolveReachablePreviousGeneration.
        const previousGenerationSha = await this.service.resolveReachablePreviousGeneration(
            preLock.current_generation ?? null,
        );

        if (options?.dryRun) {
            const existingPatches = this.service.lockManager.getPatches();
            const { patches: newPatches, revertedPatchIds: dryRunReverted } =
                await this.service.detector.detectNewPatches();
            const warnings = [...this.service.detector.warnings, ...this.service.warnings];
            const dryRunDegraded = dedupeDegradedReasons([
                ...this.service.detector.degradedReasons,
                ...this.service.degradedReasons,
            ]);
            const allPatches = [...existingPatches, ...newPatches];
            const preparedReport: ReplayReport = {
                flow: "normal-regeneration",
                patchesDetected: allPatches.length,
                patchesApplied: 0,
                patchesWithConflicts: 0,
                patchesSkipped: 0,
                patchesReverted: dryRunReverted.length,
                conflicts: [],
                wouldApply: allPatches,
                warnings: warnings.length > 0 ? warnings : undefined,
                degraded: dryRunDegraded.length > 0 ? true : undefined,
                degradedReasons: dryRunDegraded.length > 0 ? dryRunDegraded : undefined,
            };
            const headSha = await this.service.git
                .exec(["rev-parse", "HEAD"])
                .then((s) => s.trim())
                .catch(() => "");
            return {
                flow: "normal-regeneration",
                previousGenerationSha,
                currentGenerationSha: headSha,
                _prepared: {
                    kind: "terminal",
                    flow: "normal-regeneration",
                    preparedReport,
                },
            };
        }

        // Pre-generation rebase: update stale/modified patches while HEAD has customer code.
        let existingPatches = this.service.lockManager.getPatches();
        const preRebasePatchIds = new Set(existingPatches.map((p) => p.id));
        const preRebaseCounts = await this.service.preGenerationRebase(existingPatches);

        // Track which patches preGenRebase removed (user reverted → empty diff).
        // These are needed to reconcile re-detected commits and revert commits after detection.
        const postRebasePatchIds = new Set(this.service.lockManager.getPatches().map((p) => p.id));
        const removedByPreRebase = existingPatches.filter(
            (p) => preRebasePatchIds.has(p.id) && !postRebasePatchIds.has(p.id),
        );

        // Dedup patches by content hash after pre-generation rebase.
        //
        // **Consolidation, not preservation.** When two patches have
        // byte-identical `patch_content`, they encode the SAME customer-side
        // delta — neither captures its individual contribution. Applying
        // both back-to-back at the next regen produces duplicated additions
        // (e.g., the same wiring lines spliced into `__all__` twice) and
        // syntactic corruption. The customer's *file* breaks, regardless of
        // how clean the lockfile bookkeeping looks.
        //
        // PR #74's earlier "preserve both with warning" rule prioritized
        // provenance preservation over functional correctness — empirically
        // it produced the corruption we're now fixing. Per replay's actual
        // contract (the customer's code stays in the file across N regens,
        // automatically), the right move is to consolidate to ONE canonical
        // patch (the most-recently-seen, by `patchesToCommit` order) and
        // remove the others. Per-commit attribution is recoverable from
        // `git log -- <file>`; corrupted code is not.
        //
        // True duplicates (rebase converging two incremental patches) are
        // a special case of "same content_hash" — consolidating them is
        // also right; nothing changes for those tests.
        existingPatches = this.service.lockManager.getPatches();
        const seenHashes = new Map<string, string>();
        for (const p of existingPatches) {
            // Only consolidate SYNTHETIC patches (empty `original_commit`).
            // Real customer commits with distinct `original_commit` SHAs
            // represent separate user intents that should keep per-commit
            // attribution even if their `patch_content` converges to the
            // same cumulative diff after preGenerationRebase. The
            // duplicate-apply corruption case the dedup was designed to
            // catch — two .fernignore patterns producing the same synthetic
            // patch — keeps firing because those patches have empty
            // `original_commit`. See FER-9983 H3.
            if (!p.original_commit || p.original_commit === "") {
                const priorPatchId = seenHashes.get(p.content_hash);
                if (priorPatchId !== undefined) {
                    this.service.lockManager.removePatch(p.id);
                    this.service.warnings.push(
                        `Consolidated patch ${p.id}` +
                            ` into prior patch ${priorPatchId}: byte-identical patch_content. ` +
                            `Lockfile collapsed to avoid duplicate-apply corruption on next regen.`,
                    );
                } else {
                    seenHashes.set(p.content_hash, p.id);
                }
            }
        }
        existingPatches = this.service.lockManager.getPatches();

        let { patches: newPatches, revertedPatchIds } = await this.service.detector.detectNewPatches();
        // Snapshot of detector warnings; merged with service-level warnings
        // (populated by preGenerationRebase + rebasePatches) when the final
        // report is built in phase 2.
        const detectorWarnings = [...this.service.detector.warnings];
        const detectorDegraded = [...this.service.detector.degradedReasons];

        // Post-detection reconciliation for patches removed by preGenerationRebase.
        // When a user does git revert on a tracked customization, preGenRebase sees an
        // empty diff and removes the patch. detectNewPatches then re-detects both the
        // original commit AND the revert commit as new patches (since the lockfile no
        // longer tracks the original). We need to:
        // 1. Filter out re-detected originals (already handled by preGenRebase removal)
        // 2. Filter out their corresponding revert commits
        if (removedByPreRebase.length > 0) {
            const removedOriginalCommits = new Set(removedByPreRebase.map((p) => p.original_commit));
            const removedOriginalMessages = new Set(removedByPreRebase.map((p) => p.original_message));

            newPatches = newPatches.filter((p) => {
                // Filter out re-detected originals
                if (removedOriginalCommits.has(p.original_commit)) return false;
                // Filter out revert commits matching removed patches
                if (isRevertCommit(p.original_message)) {
                    const revertedMsg = parseRevertedMessage(p.original_message);
                    if (revertedMsg && removedOriginalMessages.has(revertedMsg)) return false;
                }
                return true;
            });
        }

        // Remove reverted patches from lockfile and filter from existing
        for (const id of revertedPatchIds) {
            try {
                this.service.lockManager.removePatch(id);
            } catch {
                // patch may not be in lockfile
            }
        }
        const revertedSet = new Set(revertedPatchIds);
        existingPatches = existingPatches.filter((p) => !revertedSet.has(p.id));

        const allPatches = [...existingPatches, ...newPatches];

        const commitOpts = options
            ? {
                  cliVersion: options.cliVersion ?? "unknown",
                  generatorVersions: options.generatorVersions ?? {},
              }
            : undefined;

        await this.service.committer.commitGeneration("Update SDK", commitOpts);

        // Clean up stale conflict markers from a previous crashed run.
        // HEAD is now the [fern-generated] commit with clean generated content.
        await this.service.cleanupStaleConflictMarkers();

        const genRecord = await this.service.committer.createGenerationRecord(commitOpts);

        // Add generation to lockfile BEFORE applying patches so the applicator
        // can read the current generation's tree hash for rename detection.
        this.service.lockManager.addGeneration(genRecord);

        return {
            flow: "normal-regeneration",
            previousGenerationSha,
            currentGenerationSha: genRecord.commit_sha,
            _prepared: {
                kind: "active",
                flow: "normal-regeneration",
                patchesToApply: allPatches,
                newPatchIds: new Set(newPatches.map((p) => p.id)),
                preRebaseCounts,
                detectorWarnings,
                detectorDegraded,
                revertedCount: revertedPatchIds.length,
                genSha: genRecord.commit_sha,
                optionsSnapshot: options,
            },
        };
    }

    async apply(prep: ReplayPreparation, options?: ApplyOptions): Promise<ReplayReport> {
        assertActive(prep);
        const allPatches = prep._prepared.patchesToApply;
        const newPatchIds = prep._prepared.newPatchIds;
        const detectorWarnings = prep._prepared.detectorWarnings;
        const genSha = prep._prepared.genSha;

        const results = await this.service.applicator.applyPatches(allPatches);
        this.service._lastApplyResults = results;

        // Surface "dropped context line" warnings — lines that appeared as
        // unchanged context in customer's THEIRS but were silently deleted by
        // the new generator output. diff3 sided with the deletion correctly,
        // but the customer should know so they can re-add intentionally-kept
        // lines if needed. See ThreeWayMerge.computeDroppedContextLines.
        this.service.recordDroppedContextLineWarnings(results);

        // Surface "unreachable base" warnings (INC-866) — customizations whose
        // prior-generation tree is outside the clone window, which the pipeline
        // misclassifies as new-file-both and can drop silently. Make it loud.
        this.service.recordUnreachableBaseWarnings(results);

        // Strip conflict markers from conflicting files so the commit is clean.
        // Keeps the Generated (OURS) side, preserving clean patches' changes.
        this.service.revertConflictingFiles(results);

        // Mark conflict patches as unresolved on the patch objects before adding to lockfile.
        // For new patches, this object mutation is what persists — they're appended to
        // the lockfile via addPatch() below, carrying the status through.
        for (const result of results) {
            if (result.status === "conflict") {
                result.patch.status = "unresolved";
            }
        }

        // Rebase cleanly applied patches to the current generation.
        const rebaseCounts = await this.service.rebasePatches(results, genSha);

        // Save lockfile BEFORE commit — lockfile is source of truth.
        const newPatches = allPatches.filter((p) => newPatchIds.has(p.id));
        for (const patch of newPatches) {
            if (!rebaseCounts.absorbedPatchIds.has(patch.id)) {
                this.service.lockManager.addPatch(patch);
            }
        }

        // Also mark existing (non-new) conflict patches as unresolved in the lockfile.
        // This is a DISTINCT mutation path from the object mutation above — existing
        // patches are already in the lockfile, and we need `updatePatch(id, {status})`
        // to flag them. The try/catch handles the new-patch case where the id is not
        // yet in the lockfile (object mutation above already set the status).
        for (const result of results) {
            if (result.status === "conflict") {
                try {
                    this.service.lockManager.markPatchUnresolved(result.patch.id);
                } catch {
                    // New patches already have status set via the object mutation above
                }
            }
        }

        this.service.lockManager.setFernignorePatterns(this.service.readFernignorePatterns());
        this.service.lockManager.save();
        this.service.warnings.push(...this.service.lockManager.warnings);

        if (options?.stageOnly) {
            await this.service.committer.stageAll();
        } else {
            // Always commit to persist the lockfile (which tracks unresolved patches).
            // Count only cleanly applied patches for the commit message.
            const appliedCount = results.filter((r) => r.status === "applied").length;
            const buckets = computeCommitBuckets(results, rebaseCounts.absorbedPatchIds, allPatches);
            await this.service.committer.commitReplay(appliedCount, allPatches, undefined, { buckets });
        }

        const warnings = [...detectorWarnings, ...this.service.warnings];
        const degradedReasons = [...prep._prepared.detectorDegraded, ...this.service.degradedReasons];
        return this.service.buildReport(
            "normal-regeneration",
            allPatches,
            results,
            prep._prepared.optionsSnapshot,
            warnings,
            rebaseCounts,
            prep._prepared.preRebaseCounts,
            prep._prepared.revertedCount,
            degradedReasons,
        );
    }
}
